import Fuse from 'fuse.js'
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'

import type {
  ChatSummary,
  MessageRecord,
  SearchResultRecord,
  SessionStatus,
  SyncRangeRecord,
  SyncTaskStatus,
  UserMessageSearchStatus,
} from '../shared/types'
import { browserDb } from './lib/browser-db'
import { telegramBrowser } from './lib/telegram-browser'

function toDayInput(timestamp = Date.now()) {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function dayBounds(day: string) {
  const start = new Date(`${day}T00:00:00`)
  const end = new Date(`${day}T23:59:59.999`)
  return {
    startTs: start.getTime(),
    endTs: end.getTime(),
  }
}

function formatDateTime(timestamp?: number) {
  if (!timestamp) {
    return '未同步'
  }
  return new Intl.DateTimeFormat('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(timestamp)
}

function formatDate(timestamp?: number) {
  if (!timestamp) {
    return '未同步'
  }
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(timestamp)
}

function dayLabel(day: string) {
  return formatDate(new Date(`${day}T12:00:00`).getTime())
}

function describeRangeLabel(range: SyncRangeRecord) {
  if (range.messageCount === 0) {
    return `已检查 ${formatDate(range.startTs)} - ${formatDate(range.endTs)}`
  }

  if (range.actualStartTs && range.actualEndTs) {
    return `${formatDate(range.actualStartTs)} - ${formatDate(range.actualEndTs)}`
  }

  return `${formatDate(range.startTs)} - ${formatDate(range.endTs)}`
}

function formatDuration(ms: number) {
  if (!Number.isFinite(ms) || ms <= 0) {
    return '少于 1 分钟'
  }

  const totalSeconds = Math.max(1, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}小时 ${minutes}分钟`
  }
  if (minutes > 0) {
    return `${minutes}分钟 ${seconds}秒`
  }
  return `${seconds}秒`
}

function formatBytes(bytes?: number) {
  const safeBytes = Math.max(0, bytes ?? 0)
  if (safeBytes < 1024) {
    return `${safeBytes} B`
  }
  if (safeBytes < 1024 * 1024) {
    return `${(safeBytes / 1024).toFixed(1)} KB`
  }
  if (safeBytes < 1024 * 1024 * 1024) {
    return `${(safeBytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(safeBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function getChatAvatarFallback(title?: string) {
  const normalized = (title || '').trim()
  if (!normalized) {
    return '?'
  }

  return normalized[0]?.toUpperCase?.() || normalized[0] || '?'
}

function isGroupLikeChat(chat: ChatSummary) {
  return chat.peerType === 'chat' || chat.peerType === 'channel'
}

function normalizeSenderIdInput(raw: string) {
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }

  const directMatch = trimmed.match(/^user:(\d+)$/)
  if (directMatch) {
    return `user:${directMatch[1]}`
  }

  if (/^\d+$/.test(trimmed)) {
    return `user:${trimmed}`
  }

  return null
}

function getSyncProgressMeta(task: SyncTaskStatus, nowTs: number) {
  if (task.status === 'done') {
    return {
      percent: task.progressPercent ?? 100,
      etaLabel: '已完成',
    }
  }

  if (task.status === 'cancelled') {
    return {
      percent: task.progressPercent ?? 0,
      etaLabel: '已取消',
    }
  }

  if (typeof task.progressPercent === 'number') {
    const percent = Math.max(0, Math.min(task.status === 'running' ? 99 : 100, Math.round(task.progressPercent)))
    if (task.status === 'error') {
      return {
        percent,
        etaLabel: '同步已中断',
      }
    }

    if (typeof task.etaMs === 'number' && task.etaMs > 0) {
      return {
        percent,
        etaLabel: `预计剩余 ${formatDuration(task.etaMs)}`,
      }
    }

    return {
      percent,
      etaLabel: percent > 0 ? '正在估算剩余时间' : '正在估算剩余时间',
    }
  }

  const totalSpan = Math.max(1, task.rangeEndTs - task.rangeStartTs)
  const cursorTs = typeof task.lastMessageTs === 'number'
    ? Math.min(task.rangeEndTs, Math.max(task.rangeStartTs, task.lastMessageTs))
    : task.rangeEndTs
  const coveredSpan = Math.max(0, task.rangeEndTs - cursorTs)
  const percent = Math.max(0, Math.min(99, Math.round((coveredSpan / totalSpan) * 100)))

  if (percent <= 0) {
    return {
      percent,
      etaLabel: '正在估算剩余时间',
    }
  }

  const elapsedMs = Math.max(1_000, (task.finishedAt ?? nowTs) - task.startedAt)
  const remainingMs = elapsedMs * ((100 - percent) / percent)

  return {
    percent,
    etaLabel: task.status === 'error' ? '同步已中断' : `预计剩余 ${formatDuration(remainingMs)}`,
  }
}

interface MessageWindow {
  start: number
  end: number
}

function buildRecentMessageWindow(total: number) {
  return {
    start: Math.max(0, total - 1),
    end: total,
  }
}

function mergeSearchDocs(current: SearchResultRecord[], next: SearchResultRecord[]) {
  const merged = new Map<string, SearchResultRecord>()
  for (const item of current) {
    merged.set(`${item.chatId}:${item.messageId}`, item)
  }
  for (const item of next) {
    merged.set(`${item.chatId}:${item.messageId}`, item)
  }
  return [...merged.values()].sort((a, b) => b.dateTs - a.dateTs)
}

function sumRangeSpan(ranges: Array<{ startTs: number, endTs: number }>) {
  return ranges.reduce((total, range) => total + Math.max(1, range.endTs - range.startTs), 0)
}

function normalizeActualSyncRanges(ranges: SyncRangeRecord[]) {
  const sorted = [...ranges]
    .filter((range) => typeof range.actualStartTs === 'number' && typeof range.actualEndTs === 'number' && range.messageCount > 0)
    .sort((a, b) => (a.actualStartTs ?? a.startTs) - (b.actualStartTs ?? b.startTs) || (a.actualEndTs ?? a.endTs) - (b.actualEndTs ?? b.endTs))
    .map((range) => ({
      startTs: range.actualStartTs as number,
      endTs: range.actualEndTs as number,
    }))

  if (sorted.length === 0) {
    return []
  }

  const merged = [sorted[0]]
  for (const range of sorted.slice(1)) {
    const current = merged[merged.length - 1]
    if (range.startTs <= current.endTs + 1) {
      current.endTs = Math.max(current.endTs, range.endTs)
      continue
    }
    merged.push({ ...range })
  }
  return merged
}

function subtractCoveredRanges(startTs: number, endTs: number, coveredRanges: Array<{ startTs: number, endTs: number }>) {
  const gaps: Array<{ startTs: number, endTs: number }> = []
  let cursor = startTs

  for (const range of coveredRanges) {
    if (range.endTs < cursor) {
      continue
    }
    if (range.startTs > endTs) {
      break
    }

    if (range.startTs > cursor) {
      gaps.push({
        startTs: cursor,
        endTs: Math.min(endTs, range.startTs - 1),
      })
    }

    cursor = Math.max(cursor, range.endTs + 1)
    if (cursor > endTs) {
      break
    }
  }

  if (cursor <= endTs) {
    gaps.push({ startTs: cursor, endTs })
  }

  return gaps.filter((range) => range.startTs <= range.endTs)
}

export function App() {
  const today = useMemo(() => toDayInput(), [])
  const [status, setStatus] = useState<SessionStatus>({ authorized: false, pendingStep: null, user: null })
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [authBusy, setAuthBusy] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [statusHydrated, setStatusHydrated] = useState(false)

  const [chats, setChats] = useState<ChatSummary[]>([])
  const [searchDocs, setSearchDocs] = useState<SearchResultRecord[]>([])
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [messages, setMessages] = useState<MessageRecord[]>([])
  const [ranges, setRanges] = useState<SyncRangeRecord[]>([])
  const [selectedDay, setSelectedDay] = useState(today)
  const [browseDay, setBrowseDay] = useState(today)
  const [syncStartDate, setSyncStartDate] = useState(today)
  const [syncEndDate, setSyncEndDate] = useState(today)
  const [loadingChats, setLoadingChats] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [chatQuery, setChatQuery] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResultRecord[]>([])
  const [searchPanelOpen, setSearchPanelOpen] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchFilterQuery, setUserSearchFilterQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<SearchResultRecord[]>([])
  const [userSearchTask, setUserSearchTask] = useState<UserMessageSearchStatus | null>(null)
  const [userSearchVisibleRange, setUserSearchVisibleRange] = useState<MessageWindow>(() => ({ start: 0, end: 0 }))
  const [userSearchPanelOpen, setUserSearchPanelOpen] = useState(false)
  const [searchDocsLoaded, setSearchDocsLoaded] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [task, setTask] = useState<SyncTaskStatus | null>(null)
  const [currentMessageId, setCurrentMessageId] = useState<number | null>(null)
  const [visibleRange, setVisibleRange] = useState<MessageWindow>(() => ({ start: 0, end: 0 }))
  const [surfaceError, setSurfaceError] = useState<string | null>(null)
  const [syncNowTs, setSyncNowTs] = useState(() => Date.now())
  const [syncCancelling, setSyncCancelling] = useState(false)
  const credentials = telegramBrowser.getConfiguredApiCredentials()
  const deferredSearchDocs = useDeferredValue(searchDocs)
  const messageListRef = useRef<VirtuosoHandle | null>(null)
  const searchLoadPromiseRef = useRef<Promise<void> | null>(null)

  const selectedChat = chats.find((chat) => chat.id === selectedChatId) ?? null
  const filteredChats = useMemo(() => {
    const trimmed = chatQuery.trim().toLowerCase()
    if (!trimmed) {
      return chats
    }

    return chats.filter((chat) => {
      const haystacks = [
        chat.title,
        chat.username,
        chat.peerType,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase())

      return haystacks.some((value) => value.includes(trimmed))
    })
  }, [chatQuery, chats])
  const groupChatIds = useMemo(
    () => chats.filter((chat) => isGroupLikeChat(chat)).map((chat) => chat.id),
    [chats],
  )
  const visibleMessageCount = visibleRange.end > visibleRange.start
    ? visibleRange.end - visibleRange.start + 1
    : messages.length > 0
      ? 1
      : 0
  const visibleStartLabel = messages.length === 0 ? 0 : visibleRange.start + 1
  const visibleEndLabel = messages.length === 0
    ? 0
    : Math.min(messages.length, Math.max(visibleRange.end + 1, visibleStartLabel + visibleMessageCount - 1))

  const fuse = useMemo(() => {
    return new Fuse(deferredSearchDocs, {
      includeScore: true,
      threshold: 0.38,
      ignoreLocation: true,
      minMatchCharLength: 1,
      keys: [
        { name: 'text', weight: 0.8 },
        { name: 'chatTitle', weight: 0.15 },
        { name: 'senderName', weight: 0.05 },
      ],
    })
  }, [deferredSearchDocs])
  const userSearchFuse = useMemo(() => {
    return new Fuse(userSearchResults, {
      includeScore: true,
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      keys: [
        { name: 'text', weight: 0.72 },
        { name: 'chatTitle', weight: 0.18 },
        { name: 'senderName', weight: 0.08 },
        { name: 'senderId', weight: 0.02 },
      ],
    })
  }, [userSearchResults])
  const filteredUserSearchResults = useMemo(() => {
    const trimmed = userSearchFilterQuery.trim()
    if (!trimmed) {
      return userSearchResults
    }

    return userSearchFuse.search(trimmed, { limit: 200 }).map((item) => ({
      ...item.item,
      score: item.score,
    }))
  }, [userSearchFilterQuery, userSearchResults, userSearchFuse])
  const userSearchVisibleCount = userSearchVisibleRange.end > userSearchVisibleRange.start
    ? userSearchVisibleRange.end - userSearchVisibleRange.start + 1
    : filteredUserSearchResults.length > 0
      ? 1
      : 0
  const userSearchVisibleStartLabel = filteredUserSearchResults.length === 0 ? 0 : userSearchVisibleRange.start + 1
  const userSearchVisibleEndLabel = filteredUserSearchResults.length === 0
    ? 0
    : Math.min(
        filteredUserSearchResults.length,
        Math.max(userSearchVisibleRange.end + 1, userSearchVisibleStartLabel + userSearchVisibleCount - 1),
      )

  async function hydrateLocalData() {
    const nextChats = await browserDb.getChats()
    setChats(nextChats)
    setSelectedChatId((current) => {
      if (current && nextChats.some((chat) => chat.id === current)) {
        return current
      }
      return nextChats[0]?.id ?? null
    })
  }

  async function loadStatus() {
    try {
      const next = await telegramBrowser.getStatus()
      setStatus(next)
      if (next.phone && !phone) {
        setPhone(next.phone)
      }
    }
    catch (error) {
      setAuthError(error instanceof Error ? error.message : '无法读取登录状态')
    }
    finally {
      setStatusHydrated(true)
    }
  }

  async function loadMessages(chatId: string, day: string) {
    setLoadingMessages(true)
    try {
      const { startTs, endTs } = dayBounds(day)
      const next = await browserDb.getMessagesForDay(chatId, startTs, endTs)
      setMessages(next)
      setVisibleRange(buildRecentMessageWindow(next.length))
    }
    catch (error) {
      setSurfaceError(error instanceof Error ? error.message : '加载消息失败')
      setMessages([])
      setVisibleRange({ start: 0, end: 0 })
    }
    finally {
      setLoadingMessages(false)
    }
  }

  async function loadRanges(chatId: string) {
    try {
      const next = await browserDb.getRanges(chatId)
      setRanges(next)
    }
    catch (error) {
      setSurfaceError(error instanceof Error ? error.message : '加载同步范围失败')
      setRanges([])
    }
  }

  async function ensureSearchDocsLoaded() {
    if (searchDocsLoaded) {
      return
    }

    if (searchLoadPromiseRef.current) {
      await searchLoadPromiseRef.current
      return
    }

    const task = (async () => {
      setSearchLoading(true)
      try {
        const nextDocs = await browserDb.getSearchDocuments()
        setSearchDocs(nextDocs)
        setSearchDocsLoaded(true)
      }
      catch (error) {
        setSurfaceError(error instanceof Error ? error.message : '加载搜索索引失败')
        throw error
      }
      finally {
        setSearchLoading(false)
        searchLoadPromiseRef.current = null
      }
    })()

    searchLoadPromiseRef.current = task
    await task
  }

  useEffect(() => {
    void loadStatus()
  }, [])

  useEffect(() => {
    if (!status.authorized) {
      setChats([])
      setMessages([])
      setRanges([])
      setSearchDocs([])
      setSearchDocsLoaded(false)
      setSearchLoading(false)
      setSearchPanelOpen(false)
      setUserSearchPanelOpen(false)
      setUserSearchFilterQuery('')
      setUserSearchResults([])
      setUserSearchTask(null)
      setVisibleRange({ start: 0, end: 0 })
      return
    }

    void hydrateLocalData()
  }, [status.authorized])

  useEffect(() => {
    if (!status.authorized || searchDocsLoaded || searchLoading) {
      return
    }

    const timer = window.setTimeout(() => {
      void ensureSearchDocsLoaded()
    }, 600)

    return () => window.clearTimeout(timer)
  }, [status.authorized, chats.length, searchDocsLoaded, searchLoading])

  useEffect(() => {
    if (!selectedChatId) {
      return
    }
    void loadMessages(selectedChatId, selectedDay)
    void loadRanges(selectedChatId)
  }, [selectedChatId, selectedDay])

  useEffect(() => {
    if (!status.authorized || !selectedChat || selectedChat.avatarUrl || !selectedChat.peerId) {
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const avatarUrl = await telegramBrowser.loadChatAvatar(selectedChat)
        if (!avatarUrl || cancelled) {
          return
        }

        setChats((current) => current.map((chat) => (
          chat.id === selectedChat.id
            ? { ...chat, avatarUrl }
            : chat
        )))
        await browserDb.saveChats([{ ...selectedChat, avatarUrl }])
      }
      catch (error) {
        console.warn('loadChatAvatar failed', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [status.authorized, selectedChat])

  useEffect(() => {
    if (task?.status !== 'running') {
      setSyncCancelling(false)
      return
    }

    const timer = window.setInterval(() => {
      setSyncNowTs(Date.now())
    }, 1_000)

    return () => window.clearInterval(timer)
  }, [task?.status])

  useEffect(() => {
    const list = messageListRef.current
    if (!list || messages.length === 0) {
      return
    }

    const frame = window.requestAnimationFrame(() => {
      if (currentMessageId !== null) {
        const index = messages.findIndex((message) => message.messageId === currentMessageId)
        if (index >= 0) {
          list.scrollToIndex({
            index,
            align: 'center',
            behavior: 'auto',
          })
          return
        }
      }

      list.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'auto',
      })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [messages, currentMessageId])

  useEffect(() => {
    const trimmed = searchQuery.trim()
    if (!trimmed) {
      setSearchResults([])
      return
    }

    if (!searchDocsLoaded) {
      setSearchResults([])
      void ensureSearchDocsLoaded()
      return
    }

    const timer = window.setTimeout(() => {
      const results = fuse.search(trimmed, { limit: 30 }).map((item) => ({
        ...item.item,
        score: item.score,
      }))
      setSearchResults(results)
    }, 150)

    return () => window.clearTimeout(timer)
  }, [searchDocsLoaded, searchQuery, fuse])

  useEffect(() => {
    setUserSearchVisibleRange({
      start: 0,
      end: filteredUserSearchResults.length > 0 ? 0 : 0,
    })
  }, [filteredUserSearchResults.length])

  async function handleStartLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setAuthBusy(true)
    setAuthError(null)
    try {
      await telegramBrowser.startLogin(phone.trim())
      await loadStatus()
    }
    catch (error) {
      console.error('startLogin failed', error)
      setAuthError(error instanceof Error ? error.message : '发送验证码失败')
    }
    finally {
      setAuthBusy(false)
    }
  }

  async function handleVerifyCode(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setAuthBusy(true)
    setAuthError(null)
    try {
      const next = await telegramBrowser.verifyCode(code.trim())
      setStatus(next)
      setCode('')
    }
    catch (error) {
      console.error('verifyCode failed', error)
      setAuthError(error instanceof Error ? error.message : '验证码校验失败')
    }
    finally {
      setAuthBusy(false)
    }
  }

  async function handleVerifyPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setAuthBusy(true)
    setAuthError(null)
    try {
      const next = await telegramBrowser.verifyPassword(password)
      setStatus(next)
      setPassword('')
    }
    catch (error) {
      console.error('verifyPassword failed', error)
      setAuthError(error instanceof Error ? error.message : '二步密码校验失败')
    }
    finally {
      setAuthBusy(false)
    }
  }

  async function handleRefreshChats() {
    setSurfaceError(null)
    setLoadingChats(true)
    try {
      const next = await telegramBrowser.refreshChats()
      await browserDb.saveChats(next)
      await hydrateLocalData()
    }
    catch (error) {
      setSurfaceError(error instanceof Error ? error.message : '刷新聊天失败')
    }
    finally {
      setLoadingChats(false)
    }
  }

  async function handleLogout() {
    try {
      await telegramBrowser.logout()
      await browserDb.clearAll()
      setStatus({ authorized: false, pendingStep: null, user: null })
      setTask(null)
      setSearchQuery('')
      setSearchResults([])
      setSearchPanelOpen(false)
      setUserSearchQuery('')
      setUserSearchPanelOpen(false)
      setUserSearchFilterQuery('')
      setUserSearchResults([])
      setUserSearchTask(null)
    }
    catch (error) {
      setSurfaceError(error instanceof Error ? error.message : '退出失败')
    }
  }

  async function handleSync() {
    if (!selectedChat || !selectedChat.peerId) {
      return
    }

    setSurfaceError(null)
    setSyncCancelling(false)
    let didWriteLocalData = false
    try {
      const { startTs } = dayBounds(syncStartDate)
      const { endTs } = dayBounds(syncEndDate)
      if (startTs > endTs) {
        throw new Error('开始日期不能晚于结束日期')
      }

      const existingRanges = await browserDb.getRanges(selectedChat.id)
      const pendingRanges = subtractCoveredRanges(startTs, endTs, normalizeActualSyncRanges(existingRanges))
      const totalRequestedSpan = Math.max(1, endTs - startTs)
      const pendingSpan = sumRangeSpan(pendingRanges)
      const skippedSpan = Math.max(0, totalRequestedSpan - pendingSpan)

      if (pendingRanges.length === 0) {
        setTask({
          id: `${selectedChat.id}:${Date.now()}`,
          chatId: selectedChat.id,
          status: 'done',
          processed: 0,
          saved: 0,
          startedAt: Date.now(),
          finishedAt: Date.now(),
          rangeStartTs: startTs,
          rangeEndTs: endTs,
          progressPercent: 100,
          note: '所选日期的消息已全部同步，已直接跳过。',
          lastMessageTs: startTs,
        })
        return
      }

      const aggregateTaskId = `${selectedChat.id}:${Date.now()}`
      const aggregateStartedAt = Date.now()
      const aggregateNote = skippedSpan > 0
        ? '已跳过本地已有消息覆盖的区间，只同步缺口。'
        : undefined
      let processedBase = 0
      let savedBase = 0
      let coveredPendingSpan = 0
      let mergedSearchDocs: SearchResultRecord[] = []

      for (const range of pendingRanges) {
        const completedBefore = coveredPendingSpan
        const rangeSpan = Math.max(1, range.endTs - range.startTs)

        const { task: subTask, messages: syncedMessages } = await telegramBrowser.syncChatHistory(
          selectedChat,
          range.startTs,
          range.endTs,
          (nextTask) => {
            const gapCoveredSpan = nextTask.status === 'done'
              ? rangeSpan
              : typeof nextTask.lastMessageTs === 'number'
                ? Math.max(0, range.endTs - Math.max(range.startTs, Math.min(range.endTs, nextTask.lastMessageTs)))
                : 0
            const totalCoveredSpan = Math.min(pendingSpan, completedBefore + gapCoveredSpan)
            const completedSpan = Math.min(totalRequestedSpan, skippedSpan + totalCoveredSpan)
            const progressPercent = Math.round((completedSpan / totalRequestedSpan) * 100)
            const elapsedMs = Math.max(1_000, Date.now() - aggregateStartedAt)
            const etaMs = completedSpan > 0
              ? elapsedMs * ((totalRequestedSpan - completedSpan) / completedSpan)
              : undefined

            setTask({
              id: aggregateTaskId,
              chatId: selectedChat.id,
              status: nextTask.status,
              processed: processedBase + nextTask.processed,
              saved: savedBase + nextTask.saved,
              startedAt: aggregateStartedAt,
              finishedAt: nextTask.finishedAt,
              error: nextTask.error,
              note: aggregateNote,
              progressPercent,
              etaMs,
              rangeStartTs: startTs,
              rangeEndTs: endTs,
              lastMessageTs: nextTask.lastMessageTs,
            })
          },
        )

        coveredPendingSpan += rangeSpan
        processedBase += subTask.processed
        savedBase += syncedMessages.length

        await browserDb.saveMessages(syncedMessages)
        if (syncedMessages.length > 0) {
          const actualStartTs = Math.min(...syncedMessages.map((message) => message.dateTs))
          const actualEndTs = Math.max(...syncedMessages.map((message) => message.dateTs))
          await browserDb.addSyncRange(
            selectedChat.id,
            range.startTs,
            range.endTs,
            syncedMessages.length,
            actualStartTs,
            actualEndTs,
          )
        }
        didWriteLocalData = didWriteLocalData || syncedMessages.length > 0

        if (searchDocsLoaded) {
          mergedSearchDocs = mergedSearchDocs.concat(
            syncedMessages
              .filter((message) => message.text.trim().length > 0)
              .map((message) => ({
                ...message,
                chatTitle: selectedChat.title,
              })),
          )
        }
      }

      if (searchDocsLoaded && mergedSearchDocs.length > 0) {
        setSearchDocs((current) => mergeSearchDocs(current, mergedSearchDocs))
      }

      setTask({
        id: aggregateTaskId,
        chatId: selectedChat.id,
        status: 'done',
        processed: processedBase,
        saved: savedBase,
        startedAt: aggregateStartedAt,
        finishedAt: Date.now(),
        note: aggregateNote,
        progressPercent: 100,
        etaMs: 0,
        rangeStartTs: startTs,
        rangeEndTs: endTs,
        lastMessageTs: startTs,
      })
    }
    catch (error) {
      if ((error as Error & { code?: string })?.code === 'SYNC_CANCELLED') {
        return
      }
      setSurfaceError(error instanceof Error ? error.message : '同步失败')
    }
    finally {
      if (didWriteLocalData) {
        await hydrateLocalData()
        await loadMessages(selectedChat.id, selectedDay)
      }
      await loadRanges(selectedChat.id)
    }
  }

  function handleCancelSync() {
    if (!activeTask || activeTask.status !== 'running') {
      return
    }

    const cancelled = telegramBrowser.cancelSync()
    if (!cancelled) {
      return
    }

    setSyncCancelling(true)
  }

  function handleSearchJump(item: SearchResultRecord) {
    const day = toDayInput(item.dateTs)
    setSelectedChatId(item.chatId)
    setSelectedDay(day)
    setBrowseDay(day)
    setSyncStartDate(day)
    setSyncEndDate(day)
    setCurrentMessageId(item.messageId)
  }

  function toggleSearchPanel() {
    if (!trimmedSearchQuery) {
      return
    }
    setSearchPanelOpen((current) => !current)
  }

  function toggleUserSearchPanel() {
    if (!trimmedUserSearchQuery && userSearchResults.length === 0 && userSearchTask === null) {
      return
    }
    setUserSearchPanelOpen((current) => !current)
  }

  function handleSelectChat(chatId: string) {
    setSelectedChatId(chatId)
    setCurrentMessageId(null)
    setSearchPanelOpen(false)
    setUserSearchPanelOpen(false)
  }

  async function handleLocalUserSearch() {
    const normalizedSenderId = normalizeSenderIdInput(userSearchQuery)
    if (!normalizedSenderId) {
      setSurfaceError('本地筛选目前只支持纯数字 user id，例如 123456789。')
      return
    }

    setSurfaceError(null)
    setUserSearchPanelOpen(true)
    setUserSearchFilterQuery('')
    const startedAt = Date.now()
    setUserSearchTask({
      id: `user-search-local:${startedAt}`,
      mode: 'local',
      query: userSearchQuery,
      status: 'running',
      scannedChats: 0,
      totalChats: groupChatIds.length,
      matchedMessages: 0,
      startedAt,
      resolvedSenderId: normalizedSenderId,
      note: '正在读取浏览器本地已同步消息。',
    })

    try {
      const results = await browserDb.getMessagesBySender(normalizedSenderId, groupChatIds)
      setUserSearchResults(results)
      setUserSearchTask({
        id: `user-search-local:${startedAt}`,
        mode: 'local',
        query: userSearchQuery,
        status: 'done',
        scannedChats: groupChatIds.length,
        totalChats: groupChatIds.length,
        matchedMessages: results.length,
        startedAt,
        finishedAt: Date.now(),
        resolvedSenderId: normalizedSenderId,
        note: '本地筛选已完成，只包含已经同步到浏览器里的群消息。',
      })
    }
    catch (error) {
      setSurfaceError(error instanceof Error ? error.message : '本地按用户筛选失败')
      setUserSearchTask({
        id: `user-search-local:${startedAt}`,
        mode: 'local',
        query: userSearchQuery,
        status: 'error',
        scannedChats: 0,
        totalChats: groupChatIds.length,
        matchedMessages: 0,
        startedAt,
        finishedAt: Date.now(),
        resolvedSenderId: normalizedSenderId,
        error: error instanceof Error ? error.message : '本地按用户筛选失败',
      })
    }
  }

  async function handleRemoteUserSearch() {
    const trimmed = userSearchQuery.trim()
    if (!trimmed) {
      setSurfaceError('先输入要筛选的用户 id 或 username。')
      return
    }

    if (groupChatIds.length === 0) {
      setSurfaceError('左侧还没有群聊列表，先刷新聊天后再做跨群筛选。')
      return
    }

    setSurfaceError(null)
    setUserSearchPanelOpen(true)
    setUserSearchFilterQuery('')
    setUserSearchResults([])
    setUserSearchVisibleRange({ start: 0, end: 0 })

    try {
      const { results } = await telegramBrowser.searchMessagesBySender(
        chats,
        trimmed,
        (nextTask) => {
          setUserSearchTask(nextTask)
        },
        async (batch) => {
          await browserDb.saveMessages(batch)
          setUserSearchResults((current) => mergeSearchDocs(current, batch))
          if (searchDocsLoaded) {
            setSearchDocs((current) => mergeSearchDocs(current, batch.filter((item) => item.text.trim().length > 0)))
          }
        },
      )
      await hydrateLocalData()
      setUserSearchResults(results)
      setUserSearchTask((current) => {
        if (!current || current.status !== 'done') {
          return current
        }

        return {
          ...current,
          note: `${current.note ? `${current.note} ` : ''}结果已写入浏览器本地数据库，后续可直接查本地或用顶部模糊搜索继续筛选。`,
        }
      })
    }
    catch (error) {
      if ((error as Error & { code?: string })?.code === 'USER_SEARCH_CANCELLED') {
        return
      }
      setSurfaceError(error instanceof Error ? error.message : '跨群按用户筛选失败')
    }
  }

  function handleCancelUserSearch() {
    if (userSearchTask?.status !== 'running') {
      return
    }

    const cancelled = telegramBrowser.cancelUserMessageSearch()
    if (!cancelled) {
      return
    }
  }

  function handleUserSearchJump(item: SearchResultRecord) {
    handleSearchJump(item)
  }

  function browseSelectedDay() {
    setSelectedDay(browseDay)
    setCurrentMessageId(null)
  }

  if (!statusHydrated) {
    return (
      <div className="login-shell">
        <div className="hero-panel">
          <p className="eyebrow">RESTORING SESSION</p>
          <h1>正在恢复本地 Telegram 会话。</h1>
          <p className="hero-copy">
            正在检查浏览器里保存的登录状态和本地数据。如果之前已经登录过，这里会自动跳回主界面。
          </p>
        </div>

        <div className="login-card">
          <div className="form-stack">
            <h2>请稍候</h2>
            <p className="subtle">正在连接 Telegram 并恢复本地 session...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!status.authorized) {
    return (
      <div className="login-shell">
        <div className="hero-panel">
          <p className="eyebrow">BROWSER ONLY</p>
          <h1>不启服务端，直接在浏览器里登录和同步。</h1>
          <p className="hero-copy">
            当前版本会把 Telegram session、聊天索引和同步消息都存进浏览器本地存储。日期浏览、Takeout 同步和模糊搜索全部在前端完成。
          </p>
        </div>

        <div className="login-card">
          {status.pendingStep === null && (
            <form className="form-stack" onSubmit={handleStartLogin}>
              <h2>登录 Telegram</h2>
              <label>
                <span>手机号</span>
                <input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+86 1xx xxxx xxxx" />
              </label>
              {!credentials.isConfigured && (
                <div className="error-banner">
                  缺少 Telegram API 配置。检查项目根目录下的 `.env` 或 `.env.local`，或者确认站点已包含默认的 example 配置文件。
                </div>
              )}
              <button disabled={authBusy || !credentials.isConfigured} type="submit">{authBusy ? '发送中...' : '发送验证码'}</button>
            </form>
          )}

          {status.pendingStep === 'code' && (
            <form className="form-stack" onSubmit={handleVerifyCode}>
              <h2>输入验证码</h2>
              <p className="subtle">Telegram 已向 {status.phone || phone} 发送验证码。</p>
              <label>
                <span>验证码</span>
                <input value={code} onChange={(event) => setCode(event.target.value)} placeholder="5 位或 6 位验证码" />
              </label>
              <button disabled={authBusy} type="submit">{authBusy ? '校验中...' : '验证并登录'}</button>
            </form>
          )}

          {status.pendingStep === 'password' && (
            <form className="form-stack" onSubmit={handleVerifyPassword}>
              <h2>输入二步密码</h2>
              <p className="subtle">该账号启用了 2FA，需要补充密码后完成登录。</p>
              <label>
                <span>二步密码</span>
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Telegram 2FA password" />
              </label>
              <button disabled={authBusy} type="submit">{authBusy ? '校验中...' : '完成登录'}</button>
            </form>
          )}

          {authError && <div className="error-banner">{authError}</div>}
        </div>
      </div>
    )
  }

  const trimmedSearchQuery = searchQuery.trim()
  const trimmedUserSearchQuery = userSearchQuery.trim()
  const showSearchPanel = searchPanelOpen && trimmedSearchQuery.length > 0
  const showUserSearchPanel = userSearchPanelOpen && (trimmedUserSearchQuery.length > 0 || userSearchResults.length > 0 || userSearchTask !== null)
  const activeTask = task && task.chatId === selectedChatId ? task : null
  const taskProgress = activeTask ? getSyncProgressMeta(activeTask, syncNowTs) : null
  const visibleSyncedRanges = ranges.filter(
    (range) => typeof range.actualStartTs === 'number' && typeof range.actualEndTs === 'number' && range.messageCount > 0,
  )

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-top">
          <div>
            <p className="eyebrow">Local Browser Session</p>
            <h2>{status.user?.name}</h2>
            <p className="subtle">{status.user?.phone || status.user?.username || 'Telegram account'}</p>
          </div>
          <label className="sidebar-search">
            <span>搜索会话</span>
            <input
              value={chatQuery}
              onChange={(event) => setChatQuery(event.target.value)}
              placeholder="按聊天标题筛选左侧会话"
            />
          </label>
          <div className="sidebar-actions">
            <button className="ghost-button" onClick={() => void handleRefreshChats()}>
              {loadingChats ? '刷新中...' : '刷新聊天'}
            </button>
            <button className="ghost-button" onClick={() => void handleLogout()}>
              退出
            </button>
          </div>
        </div>

        <div className="chat-list">
          {filteredChats.map((chat) => (
            <button
              key={chat.id}
              className={`chat-item ${selectedChatId === chat.id ? 'is-active' : ''}`}
              onClick={() => handleSelectChat(chat.id)}
              type="button"
            >
              <div className="chat-item-top">
                <strong>{chat.title}</strong>
                <span>{chat.peerType}</span>
              </div>
              <div className="chat-item-bottom">
                <span>{chat.syncedMessageCount} 条 · {formatBytes(chat.syncedStorageBytes)}</span>
                <span>{formatDateTime(chat.lastMessageAt)}</span>
              </div>
            </button>
          ))}

          {chats.length === 0 && !loadingChats && (
            <div className="empty-panel">
              <p>本地还没有聊天列表。</p>
              <button onClick={() => void handleRefreshChats()} type="button">读取 Telegram 聊天列表</button>
            </div>
          )}

          {chats.length > 0 && filteredChats.length === 0 && (
            <div className="empty-panel">
              <p>没有匹配到会话。</p>
              <p className="subtle">换个关键词试试，或者清空左侧搜索框。</p>
            </div>
          )}
        </div>
      </aside>

      <main className="main-shell">
        <header className="topbar">
          <div className="chat-hero">
            <div className="chat-hero-main">
              <div className="chat-hero-avatar" aria-hidden="true">
                {selectedChat?.avatarUrl
                  ? <img alt="" src={selectedChat.avatarUrl} />
                  : <span>{getChatAvatarFallback(selectedChat?.title)}</span>}
              </div>
              <div className="chat-hero-copy">
                <p className="eyebrow">Pure Browser Mode</p>
                <h1>{selectedChat?.title || '选择一个聊天'}</h1>
                {selectedChat && (
                  <p className="subtle">
                    {selectedChat.peerType} · {selectedChat.syncedMessageCount} 条已同步 · {formatBytes(selectedChat.syncedStorageBytes)}
                  </p>
                )}
              </div>
            </div>
            {selectedChat && (
              <div className="chat-hero-meta">
                <div className="chat-hero-stat">
                  <span>类型</span>
                  <strong>{selectedChat.peerType}</strong>
                </div>
                <div className="chat-hero-stat">
                  <span>已同步</span>
                  <strong>{selectedChat.syncedMessageCount} 条</strong>
                </div>
                <div className="chat-hero-stat">
                  <span>本地占用</span>
                  <strong>{formatBytes(selectedChat.syncedStorageBytes)}</strong>
                </div>
                <div className="chat-hero-stat">
                  <span>最近消息</span>
                  <strong>{formatDateTime(selectedChat.lastMessageAt)}</strong>
                </div>
              </div>
            )}
          </div>
          <div className="topbar-tools">
            <label className="search-box">
              <span>模糊搜索</span>
              <div className={`search-box-row ${trimmedSearchQuery ? 'has-toggle' : ''}`}>
                <input
                  value={searchQuery}
                  onChange={(event) => {
                    const nextValue = event.target.value
                    setSearchQuery(nextValue)
                    setSearchPanelOpen(nextValue.trim().length > 0)
                  }}
                  onFocus={() => {
                    if (trimmedSearchQuery) {
                      setSearchPanelOpen(true)
                    }
                  }}
                  placeholder="搜索本地已同步消息，点击回跳到对应聊天和日期"
                />
                {trimmedSearchQuery && (
                  <button className="ghost-button inline-toggle-button" onClick={toggleSearchPanel} type="button">
                    {showSearchPanel ? '收起结果' : '查看结果'}
                  </button>
                )}
              </div>
              <p className="subtle">只搜索已经同步到浏览器本地的消息，并可直接跳回聊天和对应日期。</p>
            </label>
            <div className="sender-search-box">
              <span>按用户筛选群聊发言</span>
              <div className={`sender-search-row ${userSearchTask?.status === 'running' ? 'has-cancel' : ''}`}>
                <input
                  value={userSearchQuery}
                  onChange={(event) => {
                    setUserSearchQuery(event.target.value)
                    setUserSearchPanelOpen(true)
                  }}
                  onFocus={() => {
                    if (trimmedUserSearchQuery || userSearchResults.length > 0 || userSearchTask !== null) {
                      setUserSearchPanelOpen(true)
                    }
                  }}
                  placeholder="输入 user id / username / t.me 链接"
                />
                <button disabled={userSearchTask?.status === 'running'} onClick={() => void handleLocalUserSearch()} type="button">
                  查本地
                </button>
                <button disabled={userSearchTask?.status === 'running' || task?.status === 'running'} onClick={() => void handleRemoteUserSearch()} type="button">
                  查全部群组
                </button>
                {userSearchTask?.status === 'running' && (
                  <button className="ghost-button" onClick={handleCancelUserSearch} type="button">
                    取消
                  </button>
                )}
              </div>
              {(trimmedUserSearchQuery || userSearchResults.length > 0 || userSearchTask !== null) && (
                <button className="ghost-button inline-toggle-button" onClick={toggleUserSearchPanel} type="button">
                  {showUserSearchPanel ? '收起结果' : '查看结果'}
                </button>
              )}
              <p className="subtle">本地只查已同步消息。查全部群组会在线遍历你的群聊，并按发送者汇总结果。</p>
            </div>
          </div>
        </header>

        {surfaceError && <div className="error-banner">{surfaceError}</div>}

        {showSearchPanel && (
          <section className="search-results">
            <div className="search-results-head">
              <strong>搜索结果</strong>
              <span>
                {searchLoading && '正在建立本地搜索索引...'}
                {!searchLoading && `${searchResults.length} 条匹配`}
              </span>
            </div>

            {searchResults.map((item) => (
              <button
                key={`${item.chatId}:${item.messageId}`}
                className="search-result"
                onClick={() => handleSearchJump(item)}
                type="button"
              >
                <div className="search-result-top">
                  <strong>{item.chatTitle}</strong>
                  <span>{formatDateTime(item.dateTs)}</span>
                </div>
                <div className="search-result-body">{item.text || '（非文本消息）'}</div>
              </button>
            ))}

            {!searchLoading && searchResults.length === 0 && (
              <div className="empty-panel">
                <p>没有匹配到已同步消息。</p>
                <p className="subtle">可以试试更短的关键词，中文单字搜索现在也支持了。</p>
              </div>
            )}
          </section>
        )}

        {showUserSearchPanel && (
          <section className="search-results user-search-results">
            <div className="search-results-head">
              <strong>按用户筛选结果</strong>
              <span>
                {userSearchTask?.status === 'running' && `已扫描 ${userSearchTask.scannedChats}/${userSearchTask.totalChats} 个群聊`}
                {userSearchTask?.status !== 'running' && `${filteredUserSearchResults.length}/${userSearchResults.length} 条匹配`}
              </span>
            </div>

            {filteredUserSearchResults.length > 0 && (
              <div className="search-results-head search-results-window-head">
                <strong>结果浏览</strong>
                <span>浏览第 {userSearchVisibleStartLabel}-{userSearchVisibleEndLabel} 条，共 {filteredUserSearchResults.length} 条</span>
              </div>
            )}

            {(userSearchResults.length > 0 || userSearchTask?.status === 'running') && (
              <label className="search-box user-result-filter-box">
                <span>在这批结果里继续搜索</span>
                <input
                  value={userSearchFilterQuery}
                  onChange={(event) => setUserSearchFilterQuery(event.target.value)}
                  placeholder="按消息内容、聊天标题、发送者继续筛选当前结果"
                />
              </label>
            )}

            {userSearchTask && (
              <div className={`task-box ${userSearchTask.status}`}>
                <strong>
                  {userSearchTask.mode === 'local' ? '本地群消息筛选' : '在线跨群筛选'}
                </strong>
                <span>输入：{userSearchTask.query}</span>
                {userSearchTask.resolvedSenderName && (
                  <span>目标用户：{userSearchTask.resolvedSenderName} ({userSearchTask.resolvedSenderId})</span>
                )}
                {!userSearchTask.resolvedSenderName && userSearchTask.resolvedSenderId && (
                  <span>目标用户：{userSearchTask.resolvedSenderId}</span>
                )}
                <span>已扫描 {userSearchTask.scannedChats}/{userSearchTask.totalChats} 个群聊，匹配 {userSearchTask.matchedMessages} 条</span>
                {userSearchTask.note && <span>{userSearchTask.note}</span>}
                {userSearchTask.mode === 'remote' && (
                  <span className="subtle">在线结果会帮你定位到对应聊天和日期；若当天还没同步，右侧仍需再点一次同步。</span>
                )}
                {userSearchTask.error && <span>{userSearchTask.error}</span>}
              </div>
            )}

            {filteredUserSearchResults.length > 0 && (
              <Virtuoso
                className="user-search-result-list"
                computeItemKey={(_, item) => `user-search:${item.chatId}:${item.messageId}`}
                data={filteredUserSearchResults}
                defaultItemHeight={116}
                increaseViewportBy={{ bottom: 800, top: 800 }}
                itemContent={(_, item) => (
                  <button
                    className="search-result"
                    onClick={() => handleUserSearchJump(item)}
                    type="button"
                  >
                    <div className="search-result-top">
                      <strong>{item.chatTitle}</strong>
                      <span>{formatDateTime(item.dateTs)}</span>
                    </div>
                    <div className="search-result-top search-result-subtop">
                      <span>{item.senderName || item.senderId || 'Unknown sender'}</span>
                      <span>#{item.messageId}</span>
                    </div>
                    <div className="search-result-body">{item.text || '（非文本消息）'}</div>
                  </button>
                )}
                overscan={{ main: 600, reverse: 600 }}
                rangeChanged={(range) => {
                  setUserSearchVisibleRange({
                    start: range.startIndex,
                    end: range.endIndex,
                  })
                }}
              />
            )}

            {!userSearchTask && userSearchResults.length === 0 && (
              <div className="empty-panel">
                <p>输入 user id 或 username 后开始筛选。</p>
                <p className="subtle">“查本地”只扫描已同步消息，“查全部群组”会在线遍历当前账号可见的群聊。</p>
              </div>
            )}

            {userSearchTask?.status !== 'running' && userSearchTask && userSearchResults.length === 0 && (
              <div className="empty-panel">
                <p>没有筛到这个用户在群里的发言。</p>
                <p className="subtle">如果你刚输入的是数字 id，可以再试一次“查全部群组”，或确认这个账号对当前登录用户可见。</p>
              </div>
            )}

            {userSearchTask?.status !== 'running' && userSearchResults.length > 0 && filteredUserSearchResults.length === 0 && (
              <div className="empty-panel">
                <p>这批结果里没有匹配到新的筛选词。</p>
                <p className="subtle">清空上面的结果内搜索框，就能恢复显示全部按用户筛出来的消息。</p>
              </div>
            )}
          </section>
        )}

        <div className="content-grid">
          <section className="messages-panel">
            <div className="panel-heading">
              <div>
                <h3>{selectedChat ? `${selectedChat.title} · ${dayLabel(selectedDay)}` : '消息'}</h3>
                <p className="subtle">聊天浏览和日期同步共用右侧同一块日期控制区。</p>
              </div>
              {messages.length > 0 && (
                <div className="message-window-tools">
                  <span>浏览第 {visibleStartLabel}-{visibleEndLabel} 条，共 {messages.length} 条</span>
                </div>
              )}
            </div>

            {!selectedChat && <div className="empty-panel"><p>先从左侧选择一个聊天。</p></div>}

            {selectedChat && loadingMessages && (
              <div className="empty-panel"><p>正在读取 {dayLabel(selectedDay)} 的消息...</p></div>
            )}

            {selectedChat && !loadingMessages && messages.length === 0 && (
              <div className="empty-panel">
                <p>{dayLabel(selectedDay)} 还没有本地消息。</p>
                <p className="subtle">直接在右侧日期选择器里把这一天或一段日期同步进来。</p>
              </div>
            )}

            {messages.length > 0 && (
              <Virtuoso
                className="message-list"
                computeItemKey={(_, message) => `${message.chatId}:${message.messageId}`}
                data={messages}
                defaultItemHeight={112}
                increaseViewportBy={{ bottom: 1200, top: 1200 }}
                initialTopMostItemIndex={{ align: 'end', index: 'LAST' }}
                itemContent={(_, message) => (
                  <article
                    className={`message-card ${currentMessageId === message.messageId ? 'is-focus' : ''}`}
                    data-message-id={message.messageId}
                  >
                    <div className="message-meta">
                      <strong>{message.senderName || message.senderId || 'Unknown sender'}</strong>
                      <span>{formatDateTime(message.dateTs)}</span>
                    </div>
                    <p>{message.text || '（非文本消息）'}</p>
                  </article>
                )}
                overscan={{ main: 800, reverse: 800 }}
                rangeChanged={(range) => {
                  setVisibleRange({
                    start: range.startIndex,
                    end: range.endIndex,
                  })
                }}
                ref={messageListRef}
              />
            )}
          </section>

          <aside className="control-panel">
            <section className="control-card">
              <p className="eyebrow">Browse</p>
              <h3>按日期浏览</h3>
              <label>
                <span>查看日期</span>
                <input type="date" value={browseDay} onChange={(event) => setBrowseDay(event.target.value)} />
              </label>
              <button disabled={!selectedChatId} onClick={browseSelectedDay} type="button">浏览当日</button>
            </section>

            <section className="control-card">
              <p className="eyebrow">Takeout Sync</p>
              <h3>按日期同步当前聊天</h3>
              <label>
                <span>开始日期</span>
                <input type="date" value={syncStartDate} onChange={(event) => setSyncStartDate(event.target.value)} />
              </label>
              <label>
                <span>结束日期</span>
                <input type="date" value={syncEndDate} onChange={(event) => setSyncEndDate(event.target.value)} />
              </label>
              <div className="sync-actions">
                <button disabled={!selectedChatId || task?.status === 'running'} onClick={() => void handleSync()} type="button">
                  {activeTask?.status === 'running' ? '同步中...' : '同步此范围'}
                </button>
                {activeTask?.status === 'running' && (
                  <button className="ghost-button" disabled={syncCancelling} onClick={handleCancelSync} type="button">
                    {syncCancelling ? '取消中...' : '取消同步'}
                  </button>
                )}
              </div>
              {activeTask && (
                <div className={`task-box ${activeTask.status}`}>
                  <strong>
                    {activeTask.status === 'running' && '正在同步'}
                    {activeTask.status === 'done' && '同步完成'}
                    {activeTask.status === 'error' && '同步失败'}
                    {activeTask.status === 'cancelled' && '已取消同步'}
                  </strong>
                  {taskProgress && (
                    <>
                      <div aria-valuemax={100} aria-valuemin={0} aria-valuenow={taskProgress.percent} className="task-progress" role="progressbar">
                        <div className="task-progress-fill" style={{ width: `${taskProgress.percent}%` }} />
                      </div>
                      <div className="task-progress-meta">
                        <span>{taskProgress.percent}%</span>
                        <span>{taskProgress.etaLabel}</span>
                      </div>
                    </>
                  )}
                  <span>已扫描 {activeTask.processed} 条，写入 {activeTask.saved} 条</span>
                  {activeTask.lastMessageTs && <span>当前批次最旧消息：{formatDateTime(activeTask.lastMessageTs)}</span>}
                  {syncCancelling && activeTask.status === 'running' && <span>正在等待当前请求收尾，然后停止同步。</span>}
                  {activeTask.note && <span>{activeTask.note}</span>}
                  <span className="subtle">进度按日期范围估算，消息分布很稀疏时会有偏差。</span>
                  {activeTask.error && <span>{activeTask.error}</span>}
                </div>
              )}
            </section>

            <section className="control-card">
              <p className="eyebrow">Synced Ranges</p>
              <h3>已同步范围</h3>
              <div className="range-list">
                {visibleSyncedRanges.map((range) => (
                  <button
                    key={range.id}
                    className="range-chip"
                    onClick={() => {
                      const day = toDayInput(range.actualStartTs as number)
                      setBrowseDay(day)
                      setSelectedDay(day)
                    }}
                    type="button"
                  >
                    <strong>{formatDate(range.actualStartTs)} - {formatDate(range.actualEndTs)}</strong>
                    <span>{range.messageCount} 条</span>
                  </button>
                ))}
                {visibleSyncedRanges.length === 0 && <p className="subtle">当前聊天还没有本地消息范围。</p>}
              </div>
            </section>
          </aside>
        </div>
      </main>
    </div>
  )
}
